/* eslint-disable react/prop-types */
import React, { useEffect, useState, useCallback } from "react";
import { useHistory } from "react-router-dom";
import Dialog from "@material-ui/core/Dialog";
import DialogContent from "@material-ui/core/DialogContent";
import { IconButton, CircularProgress } from "@material-ui/core";
import { useSelector } from "react-redux";
import cogoToast from "cogo-toast";
import {
  WhatsappShareButton,
  WhatsappIcon,
  LinkedinShareButton,
  LinkedinIcon,
} from "react-share";
import { getReferralOpportunitiesAction } from "../../../../../redux/actions/jobActions";
import { encodeShortPayload } from "../../../ShortRedirect";
import icons from "../../../../../assets/icons/icon-collection.svg";

const PostApplicationReferralModal = ({ open, onClose, excludeJobId }) => {
  const history = useHistory();
  const { userData } = useSelector(({ auth }) => auth);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [copiedId, setCopiedId] = useState(null);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    getReferralOpportunitiesAction(excludeJobId)
      .then((data) => setJobs(data.slice(0, 10)))
      .finally(() => setLoading(false));
  }, [open]);

  const buildReferralUrl = useCallback(
    (job) => {
      const baseUrl = process.env.REACT_APP_WEB_URL || window.location.origin;
      const code = encodeShortPayload({
        jobId: job.id,
        refCode: userData.refCode,
      });
      return `${baseUrl}/r/${code}`;
    },
    [userData.refCode]
  );

  const copyLink = (job) => {
    const url = buildReferralUrl(job);
    navigator.clipboard
      .writeText(url)
      .then(() => {
        setCopiedId(job.id);
        cogoToast.success("Link copied!");
        setTimeout(() => setCopiedId(null), 2000);
      })
      .catch(() => {
        cogoToast.error("Could not copy link");
      });
  };

  const openJobDetail = (job) => {
    history.push(`/home/${job.id}`);
    onClose();
  };

  const tier3Jobs = jobs.filter((j) => j.job_tier === 3);
  const tier2Jobs = jobs.filter((j) => j.job_tier === 2);
  const featuredTier3 = tier3Jobs.slice(0, 3);
  const featuredFill = Math.max(0, 3 - featuredTier3.length);
  const featuredTier2 = tier2Jobs.slice(0, featuredFill);
  const featuredJobs = [...featuredTier3, ...featuredTier2];
  const moreJobs = [
    ...tier3Jobs.slice(featuredTier3.length),
    ...tier2Jobs.slice(featuredTier2.length),
  ];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      aria-labelledby="referral-opportunities-dialog"
      PaperProps={{
        style: {
          width: "95%",
          maxWidth: "560px",
          borderRadius: "16px",
          padding: "0",
          overflow: "hidden",
        },
      }}
    >
      {/* Header */}
      <div
        style={{
          padding: "24px 24px 16px",
          borderBottom: "1px solid #F0F0F0",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <div
              style={{
                width: 44,
                height: 44,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <svg width="22" height="22" viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="11" cy="11" r="11" fill="#3A6ED4"/>
                <path d="M6 11.5L9.5 15L16 8" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <div>
              <h2
                style={{
                  margin: 0,
                  fontSize: "20px",
                  fontWeight: 700,
                  color: "#111827",
                }}
              >
                Application Successful!
              </h2>
            </div>
          </div>
          <IconButton onClick={onClose} size="small">
            <svg width="20" height="20">
              <use href={`${icons}#close`}></use>
            </svg>
          </IconButton>
        </div>

        <p
          style={{
            margin: "12px 0 4px",
            fontSize: "15px",
            fontWeight: 600,
            color: "#111827",
          }}
        >
          Now it&apos;s time to help your network level up (and get rewarded for it).
        </p>
        <p
          style={{
            margin: 0,
            fontSize: "13px",
            color: "#6B7280",
          }}
        >
          Refer top talent to active roles on workbrook. Earn 3% annual salary
          for every successful candidate you refer.
        </p>
      </div>

      <DialogContent style={{ padding: 0 }}>
        {loading && (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              padding: "40px",
            }}
          >
            <CircularProgress size={32} style={{ color: "#3A6ED4" }} />
          </div>
        )}

        {!loading && jobs.length === 0 && (
          <p
            style={{
              textAlign: "center",
              color: "#6B7280",
              padding: "40px 24px",
              fontSize: "14px",
            }}
          >
            No referral opportunities available right now. Check back soon!
          </p>
        )}

        {!loading && jobs.length > 0 && (
          <>
            {/* Featured jobs (tier 3 or first 2) with share buttons */}
            {featuredJobs.map((job) => (
              <div
                key={job.id}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  padding: "14px 24px",
                  borderBottom: "1px solid #F3F4F6",
                  gap: "12px",
                }}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <button
                    onClick={() => openJobDetail(job)}
                    style={{
                      background: "none",
                      border: "none",
                      padding: 0,
                      cursor: "pointer",
                      textAlign: "left",
                      display: "block",
                      width: "100%",
                    }}
                  >
                    <p
                      style={{
                        margin: 0,
                        fontWeight: 700,
                        fontSize: "15px",
                        color: "#111827",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {job.role_title}
                    </p>
                    <p
                      style={{
                        margin: "2px 0 0",
                        fontSize: "13px",
                        color: "#6B7280",
                      }}
                    >
                      {[job.campany_name, job.location]
                        .filter(Boolean)
                        .join(" • ")}
                    </p>
                  </button>
                </div>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    flexShrink: 0,
                  }}
                >
                  <WhatsappShareButton
                    url={buildReferralUrl(job)}
                    title={`Job opportunity: ${job.role_title}${job.campany_name ? ` at ${job.campany_name}` : ""}`}
                  >
                    <WhatsappIcon size={32} round />
                  </WhatsappShareButton>
                  <LinkedinShareButton url={buildReferralUrl(job)}>
                    <LinkedinIcon size={32} round />
                  </LinkedinShareButton>
                  <button
                    onClick={() => copyLink(job)}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                      background: copiedId === job.id ? "#28a745" : "#3A6ED4",
                      color: "#fff",
                      border: "none",
                      borderRadius: "8px",
                      padding: "8px 14px",
                      fontSize: "13px",
                      fontWeight: 600,
                      cursor: "pointer",
                      whiteSpace: "nowrap",
                      transition: "background 0.2s",
                    }}
                  >
                    <svg width="14" height="14">
                      <use href={`${icons}#link`}></use>
                    </svg>
                    {copiedId === job.id ? "Copied!" : "Copy Link & Earn"}
                  </button>
                </div>
              </div>
            ))}

            {/* More jobs — simple list with Share Link */}
            {moreJobs.length > 0 && (
              <>
                <p
                  style={{
                    textAlign: "center",
                    fontSize: "12px",
                    color: "#9CA3AF",
                    margin: "14px 0 8px",
                    letterSpacing: "0.02em",
                  }}
                >
                  View more open roles matching your network
                </p>
                {moreJobs.map((job) => (
                  <div
                    key={job.id}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      padding: "12px 24px",
                      borderTop: "1px solid #F3F4F6",
                      gap: "12px",
                    }}
                  >
                    <button
                      onClick={() => openJobDetail(job)}
                      style={{
                        background: "none",
                        border: "none",
                        padding: 0,
                        cursor: "pointer",
                        textAlign: "left",
                        flex: 1,
                        minWidth: 0,
                      }}
                    >
                      <p
                        style={{
                          margin: 0,
                          fontWeight: 600,
                          fontSize: "14px",
                          color: "#111827",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {job.role_title}
                      </p>
                      <p
                        style={{
                          margin: "2px 0 0",
                          fontSize: "12px",
                          color: "#9CA3AF",
                        }}
                      >
                        {[job.campany_name, job.location]
                          .filter(Boolean)
                          .join(" • ")}
                      </p>
                    </button>
                    <button
                      onClick={() => copyLink(job)}
                      style={{
                        background: "none",
                        border: "none",
                        padding: 0,
                        cursor: "pointer",
                        color: "#3A6ED4",
                        fontSize: "13px",
                        fontWeight: 600,
                        whiteSpace: "nowrap",
                        display: "flex",
                        alignItems: "center",
                        gap: "4px",
                        flexShrink: 0,
                      }}
                    >
                      {copiedId === job.id ? "Copied!" : "Share Link"}
                      <span style={{ fontSize: "16px" }}>›</span>
                    </button>
                  </div>
                ))}
              </>
            )}
          </>
        )}
      </DialogContent>

      {/* Footer */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "16px 24px",
          borderTop: "1px solid #F0F0F0",
        }}
      >
        <button
          onClick={() => history.push("/insights/referrals")}
          style={{
            background: "none",
            border: "none",
            padding: 0,
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            gap: "6px",
            color: "#6B7280",
            fontSize: "13px",
          }}
        >
          <svg width="16" height="16">
            <use href={`${icons}#referrals`}></use>
          </svg>
          See successful referrals
        </button>
        <div style={{ display: "flex", gap: "10px" }}>
          <button
            onClick={onClose}
            style={{
              padding: "9px 20px",
              borderRadius: "8px",
              border: "1px solid #D1D5DB",
              background: "#fff",
              color: "#374151",
              fontWeight: 600,
              fontSize: "14px",
              cursor: "pointer",
            }}
          >
            Close
          </button>
          <button
            onClick={() => {
              history.push("/insights");
              onClose();
            }}
            style={{
              padding: "9px 20px",
              borderRadius: "8px",
              border: "none",
              background: "#3A6ED4",
              color: "#fff",
              fontWeight: 600,
              fontSize: "14px",
              cursor: "pointer",
            }}
          >
            Go to Insights
          </button>
        </div>
      </div>
    </Dialog>
  );
};

export default PostApplicationReferralModal;
